import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { sortBy } from 'lodash';
import { Payment, getAssetPaymentsSubject, fetchPaymentsByAssetId } from '../../../store/Payment';
import { NewPaymentDialog } from '../../Pages/Payment/NewPaymentDialog';
import { Asset } from '../../../store/Asset';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { addDrawer } from '../../../store/Drawer';
import { Icon } from '../../Atoms/Icon';
import { formatPrice, getFormattedDate } from '../../../utils/helpers';
import { TextSpan } from '../../Atoms/Text';
import { hasDataOrHigherPermissions } from '../../../store/User';

const handlePaymentClick = (row: Payment) => {
  addDrawer({
    id: v4(),
    data: row,
    ...getMetaData({ name: ComponentName.PAYMENT, id: row.id, data: row }),
    replace: true
  });
};

const PaymentCard = ({ payment }: { payment: Payment }) => {
  return (
    <div className="flex flex-row gap-x-2 text-grey-9 text-[14px] items-center -ml-1">
      <button onClick={() => handlePaymentClick(payment)}>
        <Icon
          className="p-3 mr-1"
          type="details"
        />
      </button>
      <span>{getFormattedDate(payment.date)}</span>
      <span>{formatPrice(payment.amount)}</span>
      <span>{payment.recipientName}</span>
      <span>{payment.recipientType}</span>
    </div>
  );
};

export const AssetPayments = ({ asset }: { asset: Asset }) => {
  const disabled = !hasDataOrHigherPermissions();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [newPaymentOpen, setNewPaymentOpen] = useState(false);

  useEffect(() => {
    if (disabled || !asset.id) return;
    const subscription = getAssetPaymentsSubject().subscribe((paymentsMap) => {
      setPayments(paymentsMap[asset.id] || []);
    });
    fetchPaymentsByAssetId(asset.id);
    return () => subscription.unsubscribe();
  }, [asset.id]);

  if (disabled) return <></>;

  const sortedPayments = sortBy(payments, ['date']);

  return (
    <div className="flex flex-col gap-y-2 pl-4 py-4 border-t">
      {sortedPayments.map((payment) => (
        <PaymentCard
          key={payment.id}
          payment={payment}
        />
      ))}
      <button
        className="flex items-center space-x-2"
        onClick={() => setNewPaymentOpen(true)}
        data-testid="new-payment-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Payment
        </TextSpan>
      </button>
      <NewPaymentDialog
        open={newPaymentOpen}
        onClose={() => setNewPaymentOpen(false)}
        initialAsset={asset}
      />
    </div>
  );
};
